import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ReportsDatasheetColumn,
    ReportsDatasheetFilters,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { ReportColumnId, REPORT_COLUMN_IDS } from "org/reports/list/columns";

import { decodeOrFilter, encodeOrFilter } from "../common";

import { ReportsPageStateRaw } from ".";

type ReportsDatasheetPageStateWithType = PageStateData<"ReportsDatasheet"> & {
    type: "ReportsDatasheet";
};

function encodeReportsDatasheetPageState(
    raw: ReportsPageStateRaw
): ReportsDatasheetPageStateWithType {
    if (raw.pageType !== "ReportsDatasheet") {
        throw new Error(
            `Expected page type to be "ReportsDatasheet", but got "${raw.pageType}"`
        );
    }

    return {
        type: "ReportsDatasheet",
        version: raw.version,
        columns: Array.from(raw.visibleColumnIds).map(
            encodeReportsDatasheetColumn
        ),
        filters: encodeReportsDatasheetFilters(raw.filter),
        leftSideMenuCollapsed: raw.leftSideMenuCollapsed,
    };
}

function decodeReportsDatasheetPageState(
    input: ReportsDatasheetPageStateWithType
): ReportsPageStateRaw {
    if (input.type !== "ReportsDatasheet") {
        throw new Error(
            `Expected page type to be "ReportsDatasheet", but got "${input.type}"`
        );
    }

    const visibleColumnIdsAsArray = input.columns.map(
        decodeReportsDatasheetColumn
    );

    return {
        pageType: "ReportsDatasheet",
        version: input.version,
        visibleColumnIds: new Set(visibleColumnIdsAsArray),
        filter: decodeReportsDatasheetFilters(input.filters),
        leftSideMenuCollapsed: input.leftSideMenuCollapsed,
    };
}

function encodeReportsDatasheetColumn(
    rawColumn: ReportColumnId
): ReportsDatasheetColumn {
    switch (rawColumn) {
        case REPORT_COLUMN_IDS.DATASHEET:
            return "datasheet";
        case REPORT_COLUMN_IDS.CONTROL_PROGRAM:
            return "program";
        case REPORT_COLUMN_IDS.DATASHEET_ARCHIVED:
            return "archived";
        case REPORT_COLUMN_IDS.NAME:
        case REPORT_COLUMN_IDS.DESCRIPTION:
        case REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT:
        case REPORT_COLUMN_IDS.ACCESS_RIGHTS:
        case REPORT_COLUMN_IDS.ACTION:
        case REPORT_COLUMN_IDS.NAME_FOR_ALL:
        case REPORT_COLUMN_IDS.CLASSIFICATION:
        case REPORT_COLUMN_IDS.REPORT_TYPE:
            throw new Error(
                `Unknown column for ReportsDatasheet page: ${rawColumn}`
            );
        default:
            return assertNever(rawColumn);
    }
}

function decodeReportsDatasheetColumn(
    column: ReportsDatasheetColumn
): ReportColumnId {
    switch (column) {
        case "datasheet":
            return REPORT_COLUMN_IDS.DATASHEET;
        case "program":
            return REPORT_COLUMN_IDS.CONTROL_PROGRAM;
        case "archived":
            return REPORT_COLUMN_IDS.DATASHEET_ARCHIVED;
        default:
            return assertNever(column);
    }
}

function encodeReportsDatasheetFilters(
    filter: SimpleAndFilter | null
): ReportsDatasheetFilters {
    if (filter === null) {
        return {};
    }

    const filters = filter.filters;
    return {
        datasheet: encodeOrFilter(REPORT_COLUMN_IDS.DATASHEET, filters),
        controlProgram: encodeOrFilter(
            REPORT_COLUMN_IDS.CONTROL_PROGRAM,
            filters
        ),
        archived: encodeOrFilter(REPORT_COLUMN_IDS.DATASHEET_ARCHIVED, filters),
    };
}

function decodeReportsDatasheetFilters(
    filters: ReportsDatasheetFilters
): SimpleAndFilter | null {
    if (Object.keys(filters).length === 0) {
        return null;
    }

    const orFilters: Array<SimpleOrFilter> = [];
    if (filters.datasheet) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.DATASHEET, filters.datasheet)
        );
    }
    if (filters.controlProgram) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.CONTROL_PROGRAM,
                filters.controlProgram
            )
        );
    }
    if (filters.archived) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.DATASHEET_ARCHIVED,
                filters.archived
            )
        );
    }

    return {
        filters: orFilters,
    };
}

export { encodeReportsDatasheetPageState, decodeReportsDatasheetPageState };

export type { ReportsDatasheetPageStateWithType };
